import { useState, useEffect, useCallback } from 'react'
import { AdminUsersFilters } from '@/components/AdminUsers/AdminUsersFilters'
import { AdminUsersGrid } from '@/components/AdminUsers/AdminUsersGrid'
import { RoleEditDialog } from '@/components/AdminUsers/RoleEditDialog'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { Pagination } from '@/components/common/Pagination'
import {
  useAdminUsersList,
  useUpdateUserRole,
  useActivateUser,
  useDeactivateUser,
} from '@/hooks/useAdminUsers'
import { toast } from '@/stores/useToastStore'
import type { AdminUser, UserRole } from '@/types/adminUser'

export default function AdminUsers() {
  // Search and filter state
  const [searchInput, setSearchInput] = useState('')
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  // Role edit dialog state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)

  // Toggle status confirmation dialog
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false)
  const [userToToggle, setUserToToggle] = useState<AdminUser | null>(null)

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [searchInput, roleFilter, pageSize])

  // Queries & mutations
  const { data, isLoading, isError } = useAdminUsersList({
    search: searchInput,
    role: roleFilter,
    page,
    pageSize,
  })

  const updateRoleMutation = useUpdateUserRole()
  const activateMutation = useActivateUser()
  const deactivateMutation = useDeactivateUser()

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load users')
    }
  }, [isError])

  const users = data?.data ?? []
  const total = data?.pagination.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  // Handlers
  const handleEditRole = useCallback((user: AdminUser) => {
    setSelectedUser(user)
    setRoleDialogOpen(true)
  }, [])

  const handleRoleSubmit = useCallback(
    (role: UserRole) => {
      if (!selectedUser) return
      updateRoleMutation.mutate(
        { userId: selectedUser.id, role },
        {
          onSuccess: () => {
            setRoleDialogOpen(false)
            setSelectedUser(null)
          },
        }
      )
    },
    [selectedUser, updateRoleMutation]
  )

  const handleToggleStatusClick = useCallback((user: AdminUser) => {
    setUserToToggle(user)
    setToggleDialogOpen(true)
  }, [])

  const handleConfirmToggleStatus = useCallback(() => {
    if (!userToToggle) return

    const mutation = userToToggle.is_active
      ? deactivateMutation
      : activateMutation

    mutation.mutate(userToToggle.id, {
      onSuccess: () => {
        setToggleDialogOpen(false)
        setUserToToggle(null)
      },
    })
  }, [userToToggle, activateMutation, deactivateMutation])

  return (
    <>
      {/* Role Edit Dialog */}
      <RoleEditDialog
        open={roleDialogOpen}
        onOpenChange={setRoleDialogOpen}
        user={selectedUser}
        onSubmit={handleRoleSubmit}
        isPending={updateRoleMutation.isPending}
      />

      {/* Activate/Deactivate Confirmation Dialog */}
      <ConfirmationDialog
        open={toggleDialogOpen}
        onOpenChange={setToggleDialogOpen}
        title={userToToggle?.is_active ? 'Deactivate User' : 'Reactivate User'}
        description={
          userToToggle?.is_active
            ? `Are you sure you want to deactivate "${userToToggle?.display_name ?? userToToggle?.email ?? ''}"? They will no longer be able to access the system.`
            : `Are you sure you want to reactivate "${userToToggle?.display_name ?? userToToggle?.email ?? ''}"? They will regain access to the system.`
        }
        confirmText={userToToggle?.is_active ? 'Deactivate' : 'Reactivate'}
        cancelText="Cancel"
        onConfirm={handleConfirmToggleStatus}
        variant={userToToggle?.is_active ? 'destructive' : 'default'}
      />

      <div className="flex flex-col gap-6 p-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Manage Users
          </h1>
          <p className="text-base text-muted-foreground mt-1">
            View and manage user accounts and roles
          </p>
        </div>

        {/* Filter Bar */}
        <AdminUsersFilters
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          roleFilter={roleFilter}
          onRoleFilterChange={setRoleFilter}
        />

        {/* Users Grid */}
        <AdminUsersGrid
          users={users}
          onEditRole={handleEditRole}
          onToggleStatus={handleToggleStatusClick}
          loading={isLoading}
        />

        {/* Pagination */}
        <Pagination
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </>
  )
}
